/**
 * Light sources: animated torches / lanterns, and the torch the hero carries.
 *
 * Three things were broken and all three are fixed here:
 *
 *  1. **No shine.** A placed torch was a static prop with no light. Every source
 *     now declares a radius (`LIGHT_RADIUS_TILES`) which the farm loop hands to
 *     `DayNight.paintLights` — the same additive warm pool the campfire uses, so
 *     torchlight rides on the existing night overlay instead of fighting it.
 *  2. **No animation.** The building sprite was one trimmed frame. `lightFrames`
 *     loads the pack's real flicker loops (Torch_Anim / Big_Torch_Anim) so a
 *     placed light actually burns. The lantern is the exception — see below.
 *  3. **Can't be held.** `createHeldTorch` draws the pack's player-composited
 *     torch overlay on the hero and lights the ground around him while it burns.
 *
 * The held overlay deliberately lives OUTSIDE player.ts: the hero's own held-item
 * layer shows a single static rest frame (fine for an axe, wrong for a flame), so
 * the torch gets its own animated layer parented to the hero's root container. It
 * inherits the root's left/right flip for free and is re-stacked in front of or
 * behind the body depending on which way he faces.
 */
import { Assets, Container, Rectangle, Sprite, Texture } from 'pixi.js';
import { FRAME_H, FRAME_W } from '../character/manifest.generated';
import { TILE } from './world';
import type { Dir, PlayerHandle } from './player';

/**
 * Prop kinds that burn, and how many frames each one's loop has.
 *
 * The lantern is ONE frame on purpose. Its old three "frames" were cut from
 * columns 3-5 of Other/Lantern_Torch.png, which is the HELD-item sheet: those
 * cells are the head of the torch the hero carries in his hand, so every placed
 * Lantern building drew a hand torch. It now draws the pack's actual lamp post
 * (Outdoor decoration/Lanter_Posts.png — see scripts/build-prop-sprites.py), and
 * that sheet's six columns are pixel-identical design variants, not animation
 * frames. There is no lantern flicker loop anywhere in the packs and none may be
 * invented, so the post is static; the warm pool it throws at night is unchanged.
 */
export const LIGHT_PROPS = { torch: 8, torch_big: 8, lantern: 1 } as const;
export type LightProp = keyof typeof LIGHT_PROPS;

/** Frames-per-second each flicker loop runs at (a lantern breathes slower). */
export const LIGHT_FPS: Record<LightProp, number> = { torch: 8, torch_big: 8, lantern: 4 };

/**
 * How far each source throws light, in tiles. These are the pool radii the night
 * overlay punches — a campfire is the big one you camp around, a torch marks a
 * path, a lantern is a doorstep light.
 */
export const LIGHT_RADIUS_TILES: Record<string, number> = {
  campfire: 2.4,
  torch: 2.0,
  torch_big: 2.6,
  lantern: 1.6,
  /** The torch in the hero's hand — a touch wider than a planted one. */
  held_torch: 2.6,
};

/** Building kinds that are light sources, mapped to the prop art they animate. */
export const LIGHT_BUILDINGS: Record<string, LightProp> = { torch: 'torch', lantern: 'lantern' };

const framesCache = new Map<LightProp, Promise<Texture[]>>();

/** Load (once, shared) the flicker frames for a light prop. */
export function lightFrames(prop: LightProp): Promise<Texture[]> {
  let p = framesCache.get(prop);
  if (!p) {
    const urls = Array.from({ length: LIGHT_PROPS[prop] }, (_, i) => `/sprites/props/${prop}_${i}.png`);
    p = Assets.load(urls).then(
      (loaded: Record<string, Texture>) => urls.map((u) => loaded[u]).filter(Boolean),
      () => [],
    );
    framesCache.set(prop, p);
  }
  return p;
}

/**
 * The FURNACE fire — the one animated building that is not a light prop.
 *
 * A furnace with a load in it burns, and you can tell across the farm whether
 * yours is working; a cold one is a static sprite. The burn art is a horizontal
 * STRIP (`furnace_burn.png`, 4 frames of 16x30) rather than the per-frame PNGs
 * `lightFrames` loads, so it is sliced with Rectangle here — the same trick the
 * held-torch sheet below uses — and then played by the SAME shared animation
 * ticker the torches, the campfire and the coastline run on (c.animWater in
 * views/farm/plots.ts). One ticker, not a second one.
 *
 * OWNER (2026-08-16): "furnace is changing it's style when you are smelting ores,
 * that's bad." It was not a tint — it was a different BUILDING. The strip used to
 * be a rounder, 16x32 furnace sliced from another sheet, so lighting the fire
 * swapped the tower for a different one and grew it two pixels. The frames are now
 * generated from `furnace_lit.png` itself (scripts/build-furnace-burn.py), which is
 * the correct furnace with its vents glowing, so the burning silhouette cannot
 * disagree with the cold one — same art, same height, only the embers move.
 *
 * The height is now 32, not 30: the stations were re-cut from the pack's
 * `Furnaces.png`, which carries the SAME furnace unlit and lit as an authored
 * pair (scripts/build-stations.py). Whatever that pair's size is, all three states
 * share it — the number here just has to follow the art, and
 * `smelt-progress.test.ts` reads the real PNGs to make sure it did.
 */
export const FURNACE_BURN = { url: '/sprites/buildings/furnace_burn.png', w: 16, h: 32, frames: 4, fps: 8 } as const;

let furnaceBurn: Promise<Texture[]> | null = null;

/** Load + slice the furnace burn loop (once, shared). Empty array if it's missing. */
export function furnaceFrames(): Promise<Texture[]> {
  if (!furnaceBurn) {
    furnaceBurn = Assets.load(FURNACE_BURN.url).then(
      (sheet: Texture) => Array.from({ length: FURNACE_BURN.frames }, (_, i) => new Texture({
        source: sheet.source,
        frame: new Rectangle(i * FURNACE_BURN.w, 0, FURNACE_BURN.w, FURNACE_BURN.h),
      })),
      () => [],
    );
  }
  return furnaceBurn;
}

// ─── Held torch ───────────────────────────────────────────────────────────────
// The pack's Player/Tools/Other sheets are authored to composite straight onto a
// 64px character frame, so the hand position is already baked into the art: draw
// the matching frame at the SAME transform as the body and the torch sits in the
// hand. Layout is 6 columns x 3 rows (down / side / up), one sheet for idle and
// one for running — exactly the rows the hero's own idle/walk animations use.
const HELD_ROWS: Record<'down' | 'side' | 'up', number> = { down: 0, side: 1, up: 2 };
const HELD_SHEETS = { idle: 'Torch_Idle', walk: 'Torch_Running' } as const;
// Mirrors player.ts so the flame keeps step with the hand it is drawn onto.
const HELD_FPS = { idle: 5, walk: 9 } as const;
const CHAR_ANCHOR_Y = 0.9;
const CHAR_SCALE = (3.2 * TILE) / FRAME_H;

const facingOf = (d: Dir): 'down' | 'side' | 'up' => (d === 'down' ? 'down' : d === 'up' ? 'up' : 'side');

export interface HeldTorch {
  /** True while the torch is lit and drawn. */
  readonly lit: boolean;
  setLit(on: boolean): void;
  /** Advance the flame; call once per frame with the same dt the hero uses. */
  update(dtSec: number): void;
  destroy(): void;
}

/**
 * Attach an animated torch to the hero. Nothing is drawn until `setLit(true)`;
 * the sheets load lazily on the first light-up.
 */
export function createHeldTorch(player: PlayerHandle): HeldTorch {
  const root = player.sprite as Container;
  const sprite = new Sprite(Texture.EMPTY);
  sprite.anchor.set(0.5, CHAR_ANCHOR_Y);
  sprite.scale.set(CHAR_SCALE);
  sprite.visible = false;
  root.addChild(sprite);

  const sheets = new Map<string, Texture>();
  const frames = new Map<string, Texture>();      // `${state}:${facing}:${col}` → frame
  let loading = false;
  let lit = false;
  let state: 'idle' | 'walk' = 'idle';
  let facing: 'down' | 'side' | 'up' = 'down';
  let animT = 0, col = 0;

  const loadSheets = async () => {
    if (loading || sheets.size) return;
    loading = true;
    for (const file of Object.values(HELD_SHEETS)) {
      try { sheets.set(file, await Assets.load(`/player/tools/${file}.png`)); } catch { /* missing → no torch art */ }
    }
    loading = false;
  };

  /** Slice one 64px cell out of the matching sheet (cached). */
  const frameAt = (st: 'idle' | 'walk', face: 'down' | 'side' | 'up', i: number): Texture | null => {
    const key = `${st}:${face}:${i}`;
    const cached = frames.get(key);
    if (cached) return cached;
    const sheet = sheets.get(HELD_SHEETS[st]);
    if (!sheet) { void loadSheets(); return null; }
    const cols = Math.max(1, Math.floor(sheet.width / FRAME_W));
    const t = new Texture({
      source: sheet.source,
      frame: new Rectangle((i % cols) * FRAME_W, HELD_ROWS[face] * FRAME_H, FRAME_W, FRAME_H),
    });
    frames.set(key, t);
    return t;
  };

  const draw = () => {
    if (sprite.destroyed) return;
    const t = lit ? frameAt(state, facing, col) : null;
    if (!t) { sprite.visible = false; return; }
    sprite.texture = t;
    sprite.visible = true;
    // Facing away → the torch is on the far side of him, so draw it behind the
    // body; otherwise in front. Index 0 is the hero's own back-hand layer.
    const idx = facing === 'up' ? 0 : root.children.length - 1;
    if (root.children.includes(sprite)) root.setChildIndex(sprite, idx);
  };

  return {
    get lit() { return lit; },
    setLit(on) {
      if (on === lit) return;
      lit = on;
      if (on) { void loadSheets(); animT = 0; col = 0; }
      draw();
    },
    update(dt) {
      if (!lit || sprite.destroyed) return;
      const nextState: 'idle' | 'walk' = player.moving ? 'walk' : 'idle';
      const nextFacing = facingOf(player.dir);
      // Restart the cycle on a state/facing change, the same way the hero does —
      // that is what keeps the flame frame glued to the hand frame.
      if (nextState !== state || nextFacing !== facing) { state = nextState; facing = nextFacing; animT = 0; col = 0; }
      animT += dt;
      const fps = HELD_FPS[state];
      if (animT >= 1 / fps) {
        animT = 0;
        const sheet = sheets.get(HELD_SHEETS[state]);
        const cols = sheet ? Math.max(1, Math.floor(sheet.width / FRAME_W)) : 1;
        col = (col + 1) % cols;
      }
      draw();
    },
    destroy() {
      frames.forEach((t) => t.destroy());
      frames.clear();
      if (!sprite.destroyed) sprite.destroy();
    },
  };
}
